import React, { useState, useEffect } from 'react';

const ViewApplication = () => {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock data for a single application
    // In a real application, this would come from an API call
    const mockApplication = {
      id: 1,
      studentName: 'John Smith',
      fatherName: 'Robert Smith',
      motherName: 'Emily Smith',
      email: 'john.smith@example.com',
      phone: '+1 (555) 123-4567',
      className: 'Grade 10',
      section: 'A',
      previousSchool: 'ABC High School',
      previousClass: 'Grade 9',
      address: '123 Main Street, City, Country',
      dateOfBirth: '2008-05-15',
      gender: 'Male',
      bloodGroup: 'O+',
      guardianName: 'Robert Smith',
      guardianPhone: '+1 (555) 123-4567',
      guardianEmail: 'robert.smith@example.com',
      guardianOccupation: 'Engineer',
      guardianIncome: '$75,000/year',
      appliedDate: '2023-11-15',
      status: 'pending',
      documents: [
        { name: 'application_form.pdf', size: '245 KB', uploaded: '2023-11-15' },
        { name: 'transcript.pdf', size: '1.2 MB', uploaded: '2023-11-15' },
        { name: 'certificate.jpg', size: '567 KB', uploaded: '2023-11-15' }
      ],
      notes: 'Student has excellent academic record in previous school.'
    };

    setTimeout(() => {
      setApplication(mockApplication);
      setLoading(false);
    }, 1000); // Simulate API call delay
  }, []);

  const updateStatus = (newStatus) => {
    setApplication({ ...application, status: newStatus });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="text-center">
          <p className="text-gray-600">Application not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">View Application</h1>
          <div className="flex space-x-4">
            <button className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition">
              Print
            </button>
            <button className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-700 transition">
              Export PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Student Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Full Name</label>
                  <p className="text-gray-900">{application.studentName}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Date of Birth</label>
                  <p className="text-gray-900">{application.dateOfBirth}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Gender</label>
                  <p className="text-gray-900">{application.gender}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Blood Group</label>
                  <p className="text-gray-900">{application.bloodGroup}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Email</label>
                  <p className="text-gray-900">{application.email}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Phone</label>
                  <p className="text-gray-900">{application.phone}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-700 mb-1 font-medium">Address</label>
                  <p className="text-gray-900">{application.address}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Academic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Desired Class</label>
                  <p className="text-gray-900">{application.className}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Desired Section</label>
                  <p className="text-gray-900">{application.section}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Previous School</label>
                  <p className="text-gray-900">{application.previousSchool}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Previous Class</label>
                  <p className="text-gray-900">{application.previousClass}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Guardian Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Guardian Name</label>
                  <p className="text-gray-900">{application.guardianName}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Guardian Phone</label>
                  <p className="text-gray-900">{application.guardianPhone}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Guardian Email</label>
                  <p className="text-gray-900">{application.guardianEmail}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Occupation</label>
                  <p className="text-gray-900">{application.guardianOccupation}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-700 mb-1 font-medium">Annual Income</label>
                  <p className="text-gray-900">{application.guardianIncome}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Documents</h2>
              <div className="space-y-3">
                {application.documents.map((doc, index) => (
                  <div key={index} className="flex justify-between items-center p-3 border rounded">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-800">{doc.name}</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-gray-600 text-sm">{doc.size}</span>
                      <button className="text-primary hover:text-blue-700">View</button>
                      <button className="text-green-600 hover:text-green-800">Download</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Application Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Application ID</label>
                  <p className="text-gray-900">#{application.id.toString().padStart(6, '0')}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Applied Date</label>
                  <p className="text-gray-900">{application.appliedDate}</p>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Status</label>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    application.status === 'approved' ? 'bg-green-100 text-green-800' :
                    application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Actions</h2>
              <div className="space-y-3">
                {application.status === 'pending' && (
                  <>
                    <button
                      onClick={() => updateStatus('approved')}
                      className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition"
                    >
                      Approve Application
                    </button>
                    <button
                      onClick={() => updateStatus('rejected')}
                      className="w-full bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
                    >
                      Reject Application
                    </button>
                  </>
                )}
                <button className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-blue-700 transition">
                  Send Email
                </button>
                <button className="w-full bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition">
                  Add Note
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Notes</h2>
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-gray-700">{application.notes}</p>
              </div>
              <div className="mt-4">
                <textarea
                  className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  rows="3"
                  placeholder="Add note about this application..."
                ></textarea>
                <button className="mt-2 bg-primary text-white py-2 px-4 rounded hover:bg-blue-700 transition">
                  Add Note
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewApplication;